import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { Button } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AdvancedSearchField } from '../../component/AdvancedSearchField.tsx';
import { type SearchableField } from '../../ISearchableField.tsx';

// Selettore discendente (non `>`) perché tra il wrapper e i `Form.Item` c'è
// il div di `fieldWrapperRef` (autofocus / Enter listener).
const EditPopoverFieldWrapper = styled.div`
  &&& {
    .ant-form-item {
      margin-bottom: 0;
    }
    padding: 8px;
  }
`;

const EditPopoverFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  border-top: 1px solid ${props => props.theme.colorSplit};
`;

export interface IFilterBarEditPopoverContentBodyProps {
  field: SearchableField;
  fields: SearchableField[];
  onCancel: () => void;
}

/**
 * Corpo interno di `FilterBarEditPopoverContent` (è lui a renderizzarlo): vive
 * DENTRO al `FormHandler` annidato, così `useFormAugmentedContext` risolve sul
 * form interno (serve al listener "Enter = Applica"). Il bottone Applica usa
 * `htmlType="submit"`: submit nativo del `<form>`.
 */
export function FilterBarEditPopoverContentBody({
  field,
  fields,
  onCancel,
}: Readonly<IFilterBarEditPopoverContentBodyProps>) {
  const { t } = useTranslation();
  const augmented = useFormAugmentedContext();
  const fieldWrapperRef = useRef<HTMLDivElement>(null);

  // Autofocus al primo campo focusabile (input/combobox/switch/textarea/
  // contenteditable). Il FocusTrap del Popover esterno è già attivo al mount
  // e non rimette il focus al cambio di contenuto; lo forziamo qui.
  useEffect(() => {
    const el = fieldWrapperRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, [role="combobox"], [role="switch"], [contenteditable="true"]',
    );
    el?.focus();
  }, []);

  // Enter = Applica (scorciatoia da power user). Skip se:
  // - un widget interno ha consumato Enter (Select aperto, conferma DatePicker,
  //   …) → `defaultPrevented` true
  // - target è textarea (Enter = a capo)
  // - target è un Select/combobox (`role="combobox"`, aperto o chiuso) o un
  //   DatePicker (`aria-haspopup="dialog"`): gestiscono Enter col proprio
  //   overlay; su una multi-select chiusa Enter apre la dropdown, non fa submit
  // Per evitare l'errore a11y "static element should not be interactive" il
  // listener è attaccato via addEventListener sul ref, non come `onKeyDown`
  // inline.
  useEffect(() => {
    const node = fieldWrapperRef.current;
    if (!node) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'TEXTAREA') return;
      if (
        target?.getAttribute('role') === 'combobox' ||
        target?.getAttribute('aria-haspopup') === 'dialog'
      ) {
        return;
      }
      e.preventDefault();
      // `submitForm()` rispecchia il submit nativo del bottone Applica, ma
      // ritorna una Promise che rejecta con gli errori quando la validazione
      // fallisce (già mostrati inline da handleInvalid, popover resta aperto).
      // La catturiamo: senza `.catch` sarebbe una unhandled rejection (overlay
      // "[object Object]" in dev).
      augmented.submitForm.current?.()?.catch(() => {});
    };
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [augmented]);

  return (
    <>
      <EditPopoverFieldWrapper>
        <div ref={fieldWrapperRef}>
          <AdvancedSearchField fields={fields} index={0} field={field} />
        </div>
      </EditPopoverFieldWrapper>
      <EditPopoverFooter>
        <Button onClick={onCancel}>{t('filter-bar.cancel', 'Annulla')}</Button>
        <Button type="primary" htmlType="submit">
          {t('filter-bar.apply', 'Applica')}
        </Button>
      </EditPopoverFooter>
    </>
  );
}
